import { useEffect, useState } from 'react';
import moment from 'moment';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import BottomSheet from './ui/BottomSheet';
import PillGroup from './ui/PillGroup';

const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const arrow = 'flex h-11 w-11 items-center justify-center rounded-full text-gray-600 disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-gray-300';

// Phone-only sheet combining horizon and month scope ("This month" row).
// Both draft together and apply together so the monthly cards never refetch
// mid-choice.
export default function MonthScopeSheet({ open, onClose, horizon, horizons, year, month, onApply, now }) {
  const clock = now || moment();
  const maxYear = clock.year();
  const maxMonth = clock.month() + 1;
  const [draft, setDraft] = useState({ horizon, year, month });
  useEffect(() => { if (open) setDraft({ horizon, year, month }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const horizonOptions = horizons.map((h) => ({ value: h, label: h === 0 ? 'Intraday' : `D+${h}`, dashed: h >= 2 }));
  const monthOptions = SHORT.map((label, i) => ({ value: i + 1, label, disabled: draft.year === maxYear && i + 1 > maxMonth }));
  const monthName = moment({ year: draft.year, month: draft.month - 1 }).format('MMMM');

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="This month"
      footer={(
        <button type="button" onClick={() => { onApply(draft); onClose(); }} className="h-12 w-full rounded-xl bg-brand-600 text-[15px] font-semibold text-white hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:bg-brand-700 dark:focus-visible:ring-offset-gray-800">
          {`Show ${monthName}`}
        </button>
      )}
    >
      <div className="space-y-6 pt-2">
        <section>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Horizon</h3>
          <div className="mt-2.5"><PillGroup ariaLabel="Horizon" options={horizonOptions} value={draft.horizon} onChange={(v) => setDraft({ ...draft, horizon: v })} /></div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Dashed horizons are unpaid previews.</p>
        </section>
        <section>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Month</h3>
            <div className="flex items-center">
              <button type="button" aria-label="Previous year" disabled={draft.year <= maxYear - 2} onClick={() => setDraft({ ...draft, year: draft.year - 1 })} className={arrow}><ChevronLeftIcon className="h-5 w-5" aria-hidden="true" /></button>
              <span className="w-12 text-center font-mono text-sm text-gray-900 dark:text-gray-100">{draft.year}</span>
              <button type="button" aria-label="Next year" disabled={draft.year >= maxYear} onClick={() => setDraft({ ...draft, year: draft.year + 1, month: draft.year + 1 === maxYear ? Math.min(draft.month, maxMonth) : draft.month })} className={arrow}><ChevronRightIcon className="h-5 w-5" aria-hidden="true" /></button>
            </div>
          </div>
          <div className="mt-2 [&>div]:grid [&>div]:grid-cols-4 [&_button]:w-full">
            <PillGroup ariaLabel="Month" options={monthOptions} value={draft.month} onChange={(m) => setDraft({ ...draft, month: m })} />
          </div>
        </section>
      </div>
    </BottomSheet>
  );
}
